// js/mobile.js — Mobile-optimised day view
// Card-based layout, swipe navigation, bottom edit sheet, floating add button.
// Called from timesheet.js when window width ≤ 640 px.

import * as Cal  from './calendar.js';
import * as Data from './data.js';

// ── Detection ─────────────────────────────────────────────────────────────────

export function isMobile() {
  return window.matchMedia('(max-width: 640px)').matches;
}

// ── Entry point ───────────────────────────────────────────────────────────────

// Cache passed in from timesheet.js (already fetched there)
let _projects   = [];
let _categories = [];

export async function renderMobileTimesheet(projects, categories) {
  _projects   = projects;
  _categories = categories;

  // Always show day view on mobile
  if (Cal.getView() !== 'day') Cal.setView('day');

  const c = document.getElementById('view-timesheet');
  c.innerHTML = '';

  _renderNav(c);
  await _renderCards(c);
  _renderFAB();
  _wireSwipe(c);
}

// ── Navigation bar ────────────────────────────────────────────────────────────

function _renderNav(c) {
  const d       = Cal.getDate();
  const isToday = Cal.toISODate(d) === Cal.toISODate(new Date());

  const nav = document.createElement('div');
  nav.className = 'mob-nav';
  nav.innerHTML = `
    <button id="mob-prev" class="mob-arrow" aria-label="Previous day">&#8592;</button>
    <div class="mob-date-center">
      <div class="mob-weekday">${d.toLocaleDateString('en-GB', { weekday: 'long' })}${isToday ? ' &middot; Today' : ''}</div>
      <div class="mob-date-full">${d.toLocaleDateString('en-GB', { day: 'numeric', month: 'long', year: 'numeric' })}</div>
      ${!isToday ? '<button id="mob-today" class="mob-today-pill">Today</button>' : ''}
    </div>
    <button id="mob-next" class="mob-arrow" aria-label="Next day">&#8594;</button>
  `;

  nav.querySelector('#mob-prev').addEventListener('click', () => { Cal.goPrev();            renderMobileTimesheet(_projects, _categories); });
  nav.querySelector('#mob-next').addEventListener('click', () => { Cal.goNext();            renderMobileTimesheet(_projects, _categories); });
  nav.querySelector('#mob-today')?.addEventListener('click', () => { Cal.setDate(new Date()); renderMobileTimesheet(_projects, _categories); });

  c.appendChild(nav);
}

// ── Day cards ─────────────────────────────────────────────────────────────────

async function _renderCards(c) {
  const { from } = Cal.getCurrentRange();
  const entries  = await Data.getEntries(from, from);

  const wrap = document.createElement('div');
  wrap.className = 'mob-cards-wrap';

  const total = entries.reduce((s, e) => s + (e.duration || 0), 0);
  if (total) {
    const tot = document.createElement('div');
    tot.className = 'mob-total';
    tot.textContent = `Total: ${_fmt(total)}`;
    wrap.appendChild(tot);
  }

  if (!entries.length) {
    const empty = document.createElement('div');
    empty.className = 'mob-empty';
    empty.innerHTML = '<span>No entries yet</span><span class="mob-empty-hint">Tap + to add one</span>';
    wrap.appendChild(empty);
  } else {
    entries.forEach(entry => wrap.appendChild(_buildCard(entry, from)));
  }

  c.appendChild(wrap);
}

function _buildCard(entry, dateISO) {
  const cat  = _categories.find(c => c.id === entry.categoryId);
  const proj = _projects.find(p => p.id === entry.projectId);

  const card = document.createElement('div');
  card.className = 'mob-card';
  card.dataset.id = entry.id;

  // Left colour stripe
  const stripe = document.createElement('div');
  stripe.className = 'mob-card-stripe';
  stripe.style.background = cat?.color || '#94a3b8';
  card.appendChild(stripe);

  // Card body
  const body = document.createElement('div');
  body.className = 'mob-card-body';

  // Row 1 — category + duration
  const r1 = document.createElement('div');
  r1.className = 'mob-card-r1';
  r1.innerHTML = `
    <span class="mob-card-cat">${cat?.name || '—'}</span>
    <span class="mob-card-dur">${_fmt(entry.duration)}</span>
  `;

  // Row 2 — project + times
  const r2 = document.createElement('div');
  r2.className = 'mob-card-r2';
  r2.innerHTML = `
    <span class="mob-card-proj">${proj?.name || '—'}</span>
    <span class="mob-card-times">${entry.startTime || '—'} – ${entry.endTime || '—'}</span>
  `;

  // Row 3 — description (only if present)
  if (entry.description) {
    const r3 = document.createElement('div');
    r3.className = 'mob-card-r3';
    r3.textContent = entry.description;
    body.appendChild(r1);
    body.appendChild(r2);
    body.appendChild(r3);
  } else {
    body.appendChild(r1);
    body.appendChild(r2);
  }

  card.appendChild(body);

  // Tap → edit sheet
  card.addEventListener('click', () => _openSheet(entry, dateISO));

  return card;
}

// ── Floating add button ───────────────────────────────────────────────────────

function _renderFAB() {
  // Remove any stale FAB first
  document.getElementById('mob-fab')?.remove();

  const fab = document.createElement('button');
  fab.id = 'mob-fab';
  fab.className = 'mob-fab';
  fab.setAttribute('aria-label', 'Add time entry');
  fab.textContent = '+';

  fab.addEventListener('click', async () => {
    const { from } = Cal.getCurrentRange();
    const now  = new Date();
    const sh   = now.getHours();
    const sm   = Math.floor(now.getMinutes() / 15) * 15;
    const startTime = `${String(sh).padStart(2,'0')}:${String(sm).padStart(2,'0')}`;
    const endTime   = `${String((sh + 1) % 24).padStart(2,'0')}:${String(sm).padStart(2,'0')}`;

    const entry = await Data.saveEntry({
      date:        from,
      projectId:   _projects[0]?.id || null,
      categoryId:  _categories[0]?.id || '',
      startTime,
      endTime,
      description: '',
    });
    _openSheet(entry, from);
  });

  document.body.appendChild(fab);
}

// ── Edit sheet (slides up from bottom) ───────────────────────────────────────

function _openSheet(entry, dateISO) {
  _closeSheet();

  const overlay = document.createElement('div');
  overlay.id = 'mob-overlay';
  overlay.className = 'mob-overlay';
  overlay.addEventListener('click', e => { if (e.target === overlay) _closeSheet(); });

  const sheet = document.createElement('div');
  sheet.className = 'mob-sheet';

  const catOpts  = _categories.map(c =>
    `<option value="${c.id}" ${c.id === entry.categoryId ? 'selected' : ''}>${c.name}</option>`
  ).join('');
  const projOpts = [
    `<option value="" ${!entry.projectId ? 'selected' : ''}>— No project —</option>`,
    ..._projects.filter(p => p.active).map(p =>
      `<option value="${p.id}" ${p.id === entry.projectId ? 'selected' : ''}>${p.name}</option>`
    ),
  ].join('');

  sheet.innerHTML = `
    <div class="mob-sheet-handle"></div>
    <h3 class="mob-sheet-title">Edit Entry</h3>

    <label class="mob-sheet-label">Category</label>
    <select id="ms-cat" class="mob-sheet-input">${catOpts}</select>

    <label class="mob-sheet-label">Project</label>
    <select id="ms-proj" class="mob-sheet-input">${projOpts}</select>

    <div class="mob-sheet-times">
      <div>
        <label class="mob-sheet-label">Start</label>
        <input id="ms-start" type="time" class="mob-sheet-input" value="${entry.startTime || ''}" />
      </div>
      <div>
        <label class="mob-sheet-label">End</label>
        <input id="ms-end" type="time" class="mob-sheet-input" value="${entry.endTime || ''}" />
      </div>
    </div>

    <label class="mob-sheet-label">Description</label>
    <input id="ms-desc" type="text" class="mob-sheet-input"
      value="${(entry.description || '').replace(/"/g, '&quot;')}"
      placeholder="What did you work on?" />

    <div id="ms-error" class="mob-sheet-error" style="display:none"></div>

    <div class="mob-sheet-actions">
      <button id="ms-save"   class="mob-sheet-btn mob-sheet-save">Save</button>
      <button id="ms-delete" class="mob-sheet-btn mob-sheet-del">Delete</button>
    </div>
  `;

  overlay.appendChild(sheet);
  document.body.appendChild(overlay);

  // Animate in on next frame
  requestAnimationFrame(() => {
    overlay.classList.add('mob-overlay-show');
    sheet.classList.add('mob-sheet-show');
  });

  // Save
  sheet.querySelector('#ms-save').addEventListener('click', async () => {
    const startTime = sheet.querySelector('#ms-start').value;
    const endTime   = sheet.querySelector('#ms-end').value;
    const errEl     = sheet.querySelector('#ms-error');

    if (startTime && endTime) {
      const [sh, sm] = startTime.split(':').map(Number);
      const [eh, em] = endTime.split(':').map(Number);
      if (eh * 60 + em <= sh * 60 + sm) {
        errEl.textContent = 'End time must be after start time';
        errEl.style.display = 'block';
        sheet.querySelector('#ms-end').classList.add('mob-input-error');
        return;
      }
    }

    await Data.saveEntry({
      ...entry,
      categoryId:  sheet.querySelector('#ms-cat').value,
      projectId:   sheet.querySelector('#ms-proj').value || null,
      startTime,
      endTime,
      duration:    Data.computeDuration(startTime, endTime),
      description: sheet.querySelector('#ms-desc').value.trim(),
    });

    _closeSheet();
    await renderMobileTimesheet(_projects, _categories);
  });

  // Delete
  sheet.querySelector('#ms-delete').addEventListener('click', async () => {
    if (confirm('Delete this entry?')) {
      await Data.deleteEntry(entry.id);
      _closeSheet();
      await renderMobileTimesheet(_projects, _categories);
    }
  });

  // Auto-clear error when user changes end time
  sheet.querySelector('#ms-end').addEventListener('change', () => {
    sheet.querySelector('#ms-error').style.display = 'none';
    sheet.querySelector('#ms-end').classList.remove('mob-input-error');
  });
}

function _closeSheet() {
  document.getElementById('mob-overlay')?.remove();
}

// ── Swipe navigation ──────────────────────────────────────────────────────────

function _wireSwipe(c) {
  let _x0 = 0;
  let _y0 = 0;

  c.addEventListener('touchstart', e => {
    _x0 = e.touches[0].clientX;
    _y0 = e.touches[0].clientY;
  }, { passive: true });

  c.addEventListener('touchend', async e => {
    const dx = e.changedTouches[0].clientX - _x0;
    const dy = e.changedTouches[0].clientY - _y0;
    // Horizontal swipe: at least 60 px, more horizontal than vertical
    if (Math.abs(dx) > 60 && Math.abs(dx) > Math.abs(dy) * 1.5) {
      if (dx < 0) Cal.goNext(); else Cal.goPrev();
      await renderMobileTimesheet(_projects, _categories);
    }
  }, { passive: true });
}

// ── Util ──────────────────────────────────────────────────────────────────────

function _fmt(h) {
  if (!h) return '—';
  const hh = Math.floor(h);
  const mm = Math.round((h - hh) * 60);
  return mm ? `${hh}h ${mm}m` : `${hh}h`;
}
